/**
 * AbilityVFX — six effect archetypes keyed on `hit.kind`, three beats each.
 *
 * The archetype axis is the same one the cast animations use (CHARACTERS.md
 * §4b) and the same one the reference art is keyed on: twenty bespoke effects
 * drift the moment somebody adds an ability; six archetypes cover every
 * ability by construction, because the thing being drawn is what the ability
 * DOES to space.
 *
 * Every effect has three beats, timed by the ability's OWN numbers:
 *
 *   ANTICIPATION over `windupMs`  — something gathers on the caster. This is
 *     the telegraph an opponent reads; a zero-windup ability gets none,
 *     because drawing anticipation for the unreactable lies the other way.
 *   STRIKE at windup end          — the shape the ability occupies, at its
 *     real size, exactly when the sim resolves it. The previous version
 *     played the strike at CAST — One More Thing's shockwave fired 400ms
 *     before its damage, which is misinformation in a game with money on it.
 *   DISSIPATION over `recoveryMs` — the strike decays while the caster is
 *     committed.
 *
 * Geometry discipline is inherited from Telegraphs.js: any shape that claims
 * an area is drawn from the roster's `HitShape` numbers, and nearest-target
 * abilities (chain, swap) never get an area burst — the burst would promise
 * AoE damage that does not exist. Flourish (shards, sparks) is approximate;
 * the shapes are not.
 *
 * The LOOK is two layers. The exact SHAPES are `vfx/CelShapes.js`: hard cel
 * bands, white core → saturated body → dark rim, drawn at the ability's own
 * radius on the floor in the FLOOR-SAFE colour (`telegraphHex`). The FEEL is
 * `vfx/fx.js`: HDR sparks, cores and smoke with physics, a pooled light under
 * every beat, shock rings, scorch marks and deck chunks — in the character's
 * IDENTITY colour (`colorHex`), pushed past the bloom threshold so a shot, a
 * hit and a blast each light the deck and glow.
 */

import * as THREE from 'three/webgpu';
import { CHARACTERS, abilityAnchor } from '@chaos/protocol';
import {
  forwardFromYaw,
  GROUND_ZONE_OFFSET,
  PROJECTILE_LAUNCH_SIN,
  PROJECTILE_LAUNCH_COS,
  PROJECTILE_SPAWN_OFFSET,
  PLAYER_HALF_HEIGHT,
  DT,
  TRAP_ARM_TICKS,
  ticksToMs,
} from '@chaos/sim';
import { cameraFocus } from '../vfx/ScreenEffects.js';
import { easeOutCubic } from '../math.js';
import {
  burstRing,
  starFlash,
  shell as shellShape,
  hexRing,
  lineWall,
  zoneRing,
  boltBeam,
  smear,
  flatOnGround,
  releaseCelShape,
  getCelShapeStats,
} from '../vfx/CelShapes.js';
import { column, vortex, runeTrap, nullDome, nullFloor } from '../vfx/ZoneShapes.js';
import { hazeRing } from '../vfx/StatusShapes.js';
import { shieldHit, setHoverFloor, SLOW_HEX } from './StatusAura.js';
import { popScale, erosionAt } from '../vfx/CelLook.js';
import { getCurrentTier } from '../PostProcessing.js';
import * as fx from '../vfx/fx.js';
import { applyHitSquash } from '../PlayerVisuals.js';
// The FLOOR-SAFE tint, for the same reason Telegraphs.js uses it: these
// shapes are painted on the arena deck, and that deck already spends red on
// hazard pools and green on mend pads as rules every player reads instantly.
// A zone marker in GOOGL brick sitting for five seconds beside a hazard lane
// is a worse misread than the telegraph was, because it lasts longer.
import { telegraphHex } from '@chaos/protocol';
// Namespace import on purpose: `setFlashAmount` may not exist yet (it lands with
// the ToonMaterials flash-uniform work) and a named import of a missing export
// is a hard error under ESM. The guard in flashHitTarget makes this a no-op
// until the helper appears.
import * as ToonMaterials from '../ToonMaterials.js';
import { session, localPlayer, remotePlayers, frame } from '../state.js';
import { DECAL_LIFT, DECAL_STACK } from './decals.js';

let _scene = null;
/** Optional caster-position resolver: (casterId, outVec3) => boolean. The
 *  character mockup injects one so effects ride ITS bodies; the game falls
 *  back to state.js (local renderPos / remote meshes). */
let _resolveCaster = null;

/** Live transient meshes: { mesh, born, life, update?, sharedGeo }. */
const _live = [];
/** Scheduled strike beats: { at, fire }. Fired by updateAbilityVFX — the
 *  strike must land on the wind-up clock, not on a setTimeout the tab can
 *  starve. */
const _pending = [];

/** Ground shapes sit above telegraph decals, which sit at DECAL_LIFT. */
const FX_LIFT = DECAL_LIFT + DECAL_STACK;

/**
 * Live predicted shells, keyed `casterId:abilityId`.
 *
 * The tracer is a PREDICTION: it flies the sim's arc and detonates where the
 * arc meets the deck. The sim's shell stops at the first body it meets, which
 * for a lobbed shot is usually well short of that — so the `hit` event is the
 * true detonation, and when it arrives the tracer must NOT also detonate nine
 * units downrange. `{ detonated, until }`: the hit flips the flag, and a stale
 * entry (past `until`) is never mistaken for the next shell of the same kind.
 */
const _tracers = new Map();
/** attackerId -> when its last projectile detonated. One shell, one blast. */
const _lastDetonation = new Map();
const DETONATION_DEDUPE_MS = 120;
/** How long after a predicted shell lands its server `hit` still counts as that same blast. */
const LANDED_HIT_GRACE_MS = 400;

// ─── Quality tiers ──────────────────────────────────────────────
//
// Particle COUNTS scale with tier; the shapes themselves stay — they are the
// information. `luxe()` gates the decorative extras (secondary rings, barrage
// shell streaks) that a low-tier phone can drop without losing meaning.

const TIER_MUL = { ultra: 1, high: 0.8, medium: 0.55, low: 0.35 };

function nCount(n) {
  return Math.max(2, Math.round(n * (TIER_MUL[getCurrentTier()] ?? 1)));
}

function luxe() {
  const t = getCurrentTier();
  return t === 'ultra' || t === 'high';
}

// Cosmetic admission is independent of essential effects. Distance is measured
// from the visible subject (or detached spectator camera), not a dead body's
// stale position. Counts are scalar and queues are capped before insertion.
const DECORATION_LIMITS = Object.freeze({ maxLive: 24, maxPending: 32, maxPerWindow: 12, windowMs: 16, maxDistance: 60 });
let _decorating = false;
let _decorationLive = 0;
let _decorationPending = 0;
let _decorationWindow = -Infinity;
let _decorationCount = 0;
const _decorationStats = { admitted: 0, culledDistance: 0, culledBudget: 0 };

function allowDecoration(point, scheduling = false) {
  const focus = cameraFocus();
  const dx = point.x - focus.x;
  const dz = point.z - focus.z;
  if (dx * dx + dz * dz > DECORATION_LIMITS.maxDistance ** 2) {
    _decorationStats.culledDistance++;
    return false;
  }
  const now = performance.now();
  if (now - _decorationWindow >= DECORATION_LIMITS.windowMs || now < _decorationWindow) {
    _decorationWindow = now;
    _decorationCount = 0;
  }
  if (_decorationLive >= DECORATION_LIMITS.maxLive ||
      (scheduling && _decorationPending >= DECORATION_LIMITS.maxPending) ||
      _decorationCount >= DECORATION_LIMITS.maxPerWindow) {
    _decorationStats.culledBudget++;
    return false;
  }
  _decorationCount++;
  _decorationStats.admitted++;
  return true;
}

/**
 * The trap arms this long after it is placed (the sim's own constant), and
 * every placed trap is remembered here so the sim's `hit` for it can END its
 * marker: a Tracking Pixel that fired must not keep promising danger for the
 * rest of its 12 s. A list, not a map by caster: a 7 s cooldown under a 12 s
 * life means one caster can have two live traps, and the hit says only WHERE
 * it fired — the caster's trap nearest that point is the one that went off.
 */
const TRAP_ARM_MS = ticksToMs(TRAP_ARM_TICKS);
const _zones = [];

/**
 * The sim's own classification of a ground zone (`spawnGroundZone`): a
 * `repeat` effect makes a BARRAGE, damage without one makes a TRAP, neither
 * makes an aura. Only a trap is placed, armed and snapped here — Starlink is
 * a damaging ground zone too, and matching on damage alone drew the ice
 * "slowed" burst on every one of its six shell hits.
 */
export function isTrapAbility(ability) {
  if (!ability || ability.hit.kind !== 'ground_zone' || !(ability.damage > 0)) return false;
  return !(ability.effects ?? []).some((e) => e.kind === 'repeat');
}

function scheduleDecoration(at, point, fire) {
  if (!allowDecoration(point, true)) return;
  _decorationPending++;
  _pending.push({ at, decoration: true, fire: () => {
    if (!allowDecoration(point)) return;
    const previous = _decorating;
    _decorating = true;
    try { fire(); } finally { _decorating = previous; }
  } });
}

// ─── Body anchors ───────────────────────────────────────────────
//
// Body-anchored casts: { group, casterId, freezeAt, holdUntil }. A body
// ability RESOLVES from the caster's live position, so its VFX must ride the
// caster's rendered transform. `holdUntil` keeps the anchor alive through the
// wind-up even if every anticipation mesh has expired — the strike beat still
// needs somewhere to spawn.

const _anchors = [];
/** Set while a body-anchored beat builds, so track() parents into its group. */
let _buildAnchor = null;
const _anchorScratch = new THREE.Vector3();

export function initAbilityVFX(scene, casterResolver = null) {
  _scene = scene;
  _resolveCaster = casterResolver;
}

/**
 * A ribbon slot owned by one effect: call it to give the slot back. Idempotent,
 * because a slot freed once can be handed to another effect (a bullet) and a
 * second release would cut that one's trail.
 */
function ribbonOwner(id) {
  let freed = false;
  const free = () => {
    if (freed) return;
    freed = true;
    fx.ribbonRelease(id);
  };
  free.id = id;
  return free;
}

/**
 * Draws `mesh` for `life` ms, calling `update(mesh, t)` each frame.
 *
 * `onEnd(reason)` runs exactly once however the effect leaves: 'expired', 'cleared'
 * (a new world or match end) or 'refused' (the decoration budget turned it
 * away here). Anything the effect holds beyond its mesh — a ribbon slot — must
 * be given back there, not only inside `update`: an effect that expires across
 * a frame gap, or is cleared or refused, never runs its last update.
 */
function track(mesh, life, update, onEnd = null) {
  if (_decorating && _decorationLive >= DECORATION_LIMITS.maxLive) {
    releaseEffect({ mesh, sharedGeo: mesh.isSprite || mesh.userData.sharedGeo === true });
    _decorationStats.culledBudget++;
    onEnd?.('refused');
    return mesh;
  }
  if (_decorating) _decorationLive++;
  mesh.frustumCulled = false;
  if (_buildAnchor) {
    _buildAnchor.group.add(mesh);
    // The primitives compute world positions; the group already carries the
    // anchor origin, so re-express the mesh in caster-local space. The group
    // never rotates, so orientations survive unchanged.
    const o = _buildAnchor.group.position;
    mesh.position.x -= o.x;
    mesh.position.y -= o.y;
    mesh.position.z -= o.z;
  } else {
    _scene.add(mesh);
  }
  _live.push({
    mesh,
    born: performance.now(),
    life,
    update,
    onEnd,
    decoration: _decorating,
    sharedGeo: mesh.isSprite || mesh.userData.sharedGeo === true,
  });
  return mesh;
}

/** The caster's rendered transform — the same source the character mesh draws
 *  from, so the VFX can never be somewhere the body visibly is not. */
function casterRenderPos(casterId, out) {
  if (_resolveCaster) return _resolveCaster(casterId, out) ? out : null;
  if (casterId === session.userId) return out.copy(localPlayer.renderPos);
  const mesh = remotePlayers.get(casterId);
  return mesh ? out.copy(mesh.position) : null;
}

/**
 * No walk speed covers this in one frame (fastest kit at 15fps is ~1.4m). The
 * dash itself travels now (200 ms), so this catches what is still a jump on
 * screen: a hard correction or a respawn mid-anchor. Squared metres.
 */
const TELEPORT_JUMP2 = 2.5 * 2.5;

/**
 * `freezeAfterMs`: Blink Strike's line follows the body only up to the dash,
 * then freezes — the sim sweeps the dash from the caster's live position AT
 * RESOLVE, so the effect must be seated where the sweep started. Two triggers,
 * first one wins: the wind-up clock, and a single-frame teleport of the body
 * (the local prediction resolves the dash BEFORE the wall clock says so).
 */
function beginBodyAnchor(abilityId, casterId, origin, freezeAfterMs, holdMs) {
  const group = new THREE.Group();
  group.name = `vfx-anchor:${abilityId}:${casterId}`;
  group.position.set(origin.x, origin.y, origin.z);
  _scene.add(group);
  const now = performance.now();
  const anchor = {
    group,
    casterId,
    freezeAt: freezeAfterMs === Infinity ? Infinity : now + freezeAfterMs,
    holdUntil: now + holdMs,
  };
  _anchors.push(anchor);
  return anchor;
}

function updateAnchors(now) {
  for (let i = _anchors.length - 1; i >= 0; i--) {
    const a = _anchors[i];
    // All child effects expired AND no beat is still waiting to spawn into it.
    if (a.group.children.length === 0 && now >= a.holdUntil) {
      _scene.remove(a.group);
      _anchors.splice(i, 1);
      continue;
    }
    if (now >= a.freezeAt) continue;
    // A dead/vanished caster keeps the last position — the effect finishes
    // where the body was last drawn instead of snapping to a stale origin.
    if (!casterRenderPos(a.casterId, _anchorScratch)) continue;
    // Finite-freeze anchors (the dash line) also freeze on a teleport-sized
    // jump: that IS the dash, seen through prediction ahead of the clock.
    if (
      a.freezeAt !== Infinity &&
      a.group.position.distanceToSquared(_anchorScratch) > TELEPORT_JUMP2
    ) {
      a.freezeAt = 0;
      continue;
    }
    a.group.position.copy(_anchorScratch);
  }
}

// ─── Animated shape spawners ────────────────────────────────────
//
// Each wraps a CelShapes builder with its motion curve and lifetime. `hold`
// then fade is the house rule for anything that marks live danger — an
// indicator that fades the whole time reads as "already gone" while it kills.

/**
 * The exact-radius shock band: a THICK filled ring that grows from a third of
 * the radius to the true edge fast, STOPS there (the footprint is the
 * information), then erodes away. A huge zone's band is held back a little
 * so it never walls off the fight.
 */
function popRing(x, y, z, radius, colorHex, life = 480, { reverse = false } = {}) {
  const bandStart = 1 - Math.min(0.42, Math.max(0.16, 2.1 / radius));
  const { mesh, u } = burstRing(colorHex, { bandStart });
  flatOnGround(mesh, x, y, z);
  mesh.renderOrder = 6;
  const grow = Math.min(0.5, 150 / life);
  const ceiling = radius >= 6 ? 0.8 : 1;
  return track(mesh, life, (m, t) => {
    const k = reverse
      ? 1 - easeOutCubic(Math.min(t / 0.85, 1)) * 0.9 // implosion: radius → the centre
      : 0.34 + 0.66 * easeOutCubic(Math.min(t / grow, 1)); // burst: out to the true edge, then hold
    m.scale.setScalar(Math.max(0.06, radius * k));
    u.fade.value = ceiling;
    u.erode.value = erosionAt(t, reverse ? 0.75 : 0.45);
  });
}

/** The billboarded impact burst: pops in oversize on frame one, erodes out. */
function popStar(x, y, z, size, colorHex, life = 240, { additive = false, gain = 1 } = {}) {
  const { mesh, u } = starFlash(colorHex, { additive });
  mesh.position.set(x, y, z);
  mesh.renderOrder = 9;
  // An impact sits ON the body it hit; it must draw over it.
  mesh.material.depthTest = false;
  if (u.gain) u.gain.value = gain;
  return track(mesh, life, (m, t) => {
    m.scale.setScalar(size * popScale(t, 0.2, 0.35));
    u.erode.value = erosionAt(t, 0.3);
  });
}

/** A star that GROWS over the wind-up — the universal charge glint. */
function chargeStar(x, y, z, size, colorHex, windupMs) {
  const { mesh, u } = starFlash(colorHex, { additive: true });
  mesh.position.set(x, y, z);
  mesh.renderOrder = 8;
  return track(mesh, windupMs + 60, (m, t) => {
    const k = Math.min(t * (windupMs + 60) / windupMs, 1);
    m.scale.setScalar(Math.max(0.05, size * easeOutCubic(k)));
    u.spin.value += 0.06;
    u.fade.value = t > 0.85 ? (1 - t) / 0.15 : 0.9;
  });
}

/** A ring that CONTRACTS from the ability's radius onto the caster — the
 *  sphere archetype's anticipation, drawn at the radius it will strike at. */
function gatherRing(x, y, z, radius, colorHex, windupMs) {
  const bandStart = 1 - Math.min(0.42, Math.max(0.16, 2.1 / radius));
  const { mesh, u } = burstRing(colorHex, { bandStart });
  flatOnGround(mesh, x, y, z);
  mesh.renderOrder = 6;
  return track(mesh, windupMs + 60, (m, t) => {
    const k = Math.min(t * (windupMs + 60) / windupMs, 1);
    m.scale.setScalar(Math.max(0.06, radius * (1 - easeOutCubic(k) * 0.9)));
    u.fade.value = 0.35 + 0.4 * k;
  });
}

/**
 * A vertical cel column — the chain's discharge, the fault line's eruptions,
 * a swap's departure. The one shape that reads at any camera pitch: a
 * pooled ZoneShapes `column` sprite (pass 1's was a fresh additive cylinder
 * per cast, a milky cup from 55° and a shader compile on its first use).
 * Pops in oversize, erodes out from the tip.
 */
function pillar(x, y, z, width, height, colorHex, life = 420) {
  const { mesh, u } = column(colorHex);
  mesh.position.set(x, y + height * 0.5, z);
  mesh.renderOrder = 9;
  // Its core stays UNDER the bloom threshold (2.4 × 0.6 < 2.0): three
  // discharge columns at once turned the phone's chain into three halos.
  u.gain.value = 0.6;
  return track(mesh, life, (m, t) => {
    const k = popScale(t, 0.22, 0.35);
    m.scale.set(width * k, height * k, 1);
    u.erode.value = erosionAt(t, 0.4);
  });
}

/**
 * Deindexed's field: a faceted lattice dome (ink at the silhouette, a hot
 * band at its foot) over a darkened, hatched floor with a scan sweeping in,
 * inside the zone's own boundary band. Held whole until the last 15% and
 * then eroded — a field that fades the whole time reads as expired while it
 * is still suppressing.
 */
function nullField(x, groundY, z, radius, floorHex, life) {
  const gy = groundY + FX_LIFT;
  const { mesh: floor, u: fu } = nullFloor(floorHex);
  flatOnGround(floor, x, gy, z);
  floor.scale.setScalar(radius);
  floor.renderOrder = 4;
  track(floor, life, (m, t) => {
    fu.erode.value = t > 0.85 ? erosionAt(t, 0.85) : 0;
  });
  const { mesh: dome, u: du } = nullDome(floorHex);
  dome.position.set(x, groundY, z);
  dome.renderOrder = 7;
  track(dome, life, (m, t) => {
    const k = popScale(t * (life / 220), 1, 0.12);
    m.scale.set(radius * k, radius * 0.72 * k, radius * k);
    du.erode.value = t > 0.85 ? erosionAt(t, 0.85) : 0;
  });
}

// ─── The six archetypes ─────────────────────────────────────────
//
// ctx: { ability, abilityId, characterId, color, origin {x,y,z}, groundY,
//        yaw, fwd {x,z}, windupMs, recoveryMs, tail }
// `origin.y` is the capsule centre; ground shapes use ctx.groundY + FX_LIFT.
// Anticipations run during the wind-up; strikes run AT resolve, from the
// anchor's live position for body abilities.

const ARCHETYPES = {
  melee_cone: {
    anticipate(ctx) {
      const { origin, color, windupMs } = ctx;
      // A glint at the weapon hand — the cheap read for the shortest wind-up
      // in the game. Inward shards only when the wind-up is long enough for
      // anyone to see them arrive.
      chargeStar(origin.x, origin.y + 0.5, origin.z, 0.8, color, windupMs);
      if (windupMs >= 200) gatherShards(ctx, 1.6, nCount(6));
    },
    strike(ctx) {
      const { ability, origin, groundY, color, floor, fwd, heavy } = ctx;
      // The swing is the kit's: the cel arc in the identity colour wiping
      // across the sector, sparks off its tip, a light under it — and for the
      // tank, deck dust, a floor ring and a real shake. One implementation for
      // the roster's melee basics and for anything the projectile lane calls.
      fx.slash(origin, fwd, color, ability.hit.range, (ability.hit.halfAngleDeg * Math.PI) / 180, {
        heavy,
        floorColor: floor,
        floorY: groundY,
      });
    },
  },

  sphere: {
    anticipate(ctx) {
      const { ability, origin, groundY, color, windupMs } = ctx;
      // The gather draws the ability's OWN radius contracting onto the caster
      // — "this whole circle, soon". Nearest-target abilities (chain, swap)
      // skip it: their telegraph rim + reticle already say the honest thing,
      // and a contracting AoE ring would say the dishonest one.
      if (!nearestOnly(ctx.ability)) {
        gatherRing(origin.x, groundY + FX_LIFT, origin.z, ability.hit.radius, ctx.floor, windupMs);
      }
      chargeStar(origin.x, origin.y + 0.9, origin.z, 1.4, color, windupMs);
      gatherShards(ctx, Math.min(ability.hit.radius * 0.5, 4), nCount(10));
    },
    strike(ctx) {
      const { ability, origin, groundY, color, floor, tail, heavy } = ctx;
      const radius = ability.hit.radius;
      const gy = groundY + FX_LIFT;
      if (nearestOnly(ability)) {
        // Chain Lightning / Robotaxi strike ONE target — the arcs and swap
        // columns land per `hit` event. Here: only the caster's own discharge:
        // a cel column bursting up off the body, a star, sparks and a light.
        pillar(origin.x, groundY, origin.z, 1.7, 4.2, color, 420);
        popStar(origin.x, origin.y + 1.2, origin.z, 2.4, color, 260);
        fx.burst(origin, color, { count: 12, speed: 3.5, floorY: groundY });
        fx.flash({ x: origin.x, y: origin.y + 0.8, z: origin.z }, color, 18, 6, 260);
        return;
      }
      const pull = (ability.effects ?? []).some((e) => e.kind === 'pull');
      if (pull) {
        // PageRank: the well has to visibly PULL. A spiral vortex turning
        // into a dark centre on the floor at the true radius, the footprint
        // band imploding, shards streaking in point-first over the pull's
        // whole second, dust dragged in along the deck, a light in the well.
        const pullMs = 1000;
        const { mesh, u } = vortex(floor);
        flatOnGround(mesh, origin.x, gy + DECAL_STACK, origin.z);
        mesh.renderOrder = 5;
        track(mesh, pullMs + tail, (m, t) => {
          m.scale.setScalar(radius * popScale(t, 0.18, 0.2));
          u.erode.value = erosionAt(t, 0.72);
        });
        popRing(origin.x, gy, origin.z, radius, floor, 220 + tail, { reverse: true });
        popStar(origin.x, origin.y + 0.4, origin.z, Math.min(radius * 0.35, 2.2), color, 240);
        fx.implode({ x: origin.x, y: gy + 0.4, z: origin.z }, radius, color, 22, pullMs / 1000);
        fx.implode({ x: origin.x, y: gy + 0.3, z: origin.z }, radius * 0.7, color, 12, pullMs / 1000 * 0.6);
        fx.drag(origin.x, gy, origin.z, radius, nCount(12), pullMs / 1000);
        fx.flash({ x: origin.x, y: gy + 0.8, z: origin.z }, color, 24, radius, pullMs);
        return;
      }
      // A slam for the juggernaut (dust wall, deck chunks, a shove of shake),
      // a detonation for everyone else. Both draw their band at EXACTLY the
      // ability's radius — the band is the footprint, not decoration — and
      // both carry their own burst star in the middle of it.
      const at = { x: origin.x, y: gy, z: origin.z };
      if (heavy) fx.slam(at, radius, color, { floorColor: floor, floorY: gy });
      else fx.explosion(at, radius, color, { floorColor: floor, floorY: gy, big: radius >= 8 });
    },
  },

  dash_line: {
    anticipate(ctx) {
      const { origin, color, windupMs } = ctx;
      // The coil: energy converging on the caster plus a brightening glint.
      // The ground line itself is the telegraph's job — exact, already drawn.
      chargeStar(origin.x, origin.y + 0.4, origin.z, 1.1, color, windupMs);
      gatherShards(ctx, 2.0, nCount(8));
    },
    strike(ctx) {
      const { ability, origin, groundY, color, floor, yaw, fwd, tail } = ctx;
      const len = ability.selfMove && ability.selfMove > 0 ? ability.selfMove : ability.hit.length;
      const width = ability.hit.width;
      const gy = groundY + FX_LIFT;
      const stun = (ability.effects ?? []).some((e) => e.kind === 'stun');
      // The line at its authored length and width, pulse head travelling the
      // whole length. Fault Line's pulse takes ~300ms (the ground cracking
      // open); Parallel Path's runs on the dash's own travel time, so the head
      // stays level with the body the sim is carrying down the line.
      const pulseMs = stun ? 300 : ability.selfMoveMs ?? 110;
      const life = pulseMs + 220 + tail;
      const { mesh, u } = lineWall(len, width, floor);
      flatOnGround(mesh, origin.x + fwd.x * (len / 2), gy, origin.z + fwd.z * (len / 2), yaw);
      mesh.renderOrder = 6;
      track(mesh, life, (m, t) => {
        u.progress.value = Math.min((t * life) / pulseMs, 1);
        u.erode.value = erosionAt(t, 0.55);
      });
      const end = { x: origin.x + fwd.x * len, y: origin.y, z: origin.z + fwd.z * len };
      if (stun) popStar(end.x, end.y, end.z, 1.8, color, 260);
      if (stun) {
        // Fault Line: the deck cracks under the caster, then eruptions march
        // down the crack on the pulse's own clock — each a light, a spray of
        // sparks and chunks, and a column.
        fx.dust(origin.x, gy, origin.z, 10, 2);
        fx.chunks({ x: origin.x, y: gy, z: origin.z }, floor, 4, gy);
        fx.flash({ x: origin.x, y: gy + 0.8, z: origin.z }, color, 22, 7, 300);
        const steps = luxe() ? 5 : 3;
        for (let i = 1; i <= steps; i++) {
          const f = i / steps;
          const ex = origin.x + fwd.x * len * f;
          const ez = origin.z + fwd.z * len * f;
          scheduleDecoration(performance.now() + pulseMs * f, { x: ex, z: ez }, () => {
            pillar(ex, groundY, ez, 1.4, 2.8, color, 380);
            fx.impact({ x: ex, y: gy + 0.3, z: ez }, color, { count: 8, floorY: gy });
            fx.chunks({ x: ex, y: gy, z: ez }, floor, 3, gy);
            fx.shockRing({ x: ex, y: gy, z: ez }, width * 0.6, floor, 300);
          });
        }
      } else if (!((ability.selfMoveMs ?? 0) > 0)) {
        // An instant dash (nothing authored travels like this now): a streak
        // of sparks down the whole line and a burst at the far end, lit.
        popStar(end.x, end.y, end.z, 1.8, color, 260);
        fx.streak(origin, end, color, 14);
        fx.impact(end, color, { count: 10, floorY: groundY });
        fx.flash(end, color, 12, 5, 200);
      }
      // Parallel Path TRAVELS: the streak rides the body and the burst lands
      // where the body actually stops — `playTravel`, from the sim's `blink`.
    },
  },

  projectile: {
    anticipate(ctx) {
      const { origin, color, windupMs, fwd } = ctx;
      // The charge gathers at the MUZZLE, down the frozen aim — from overhead,
      // where the glint sits is the aim readout.
      const mx = origin.x + fwd.x * 0.9;
      const mz = origin.z + fwd.z * 0.9;
      chargeStar(mx, origin.y + 0.5, mz, 1.0, color, windupMs);
    },
    strike(ctx) {
      const { ability, origin, color, floor, fwd } = ctx;
      const mx = origin.x + fwd.x * 0.9;
      const mz = origin.z + fwd.z * 0.9;
      // The muzzle: the spike flash down the barrel, sparks, a puff, a light.
      fx.muzzle({ x: mx, y: origin.y + 0.5, z: mz }, fwd, color, 1.4);
      // The shell itself flies from resolve — the sim spawns it at resolve.
      ballisticTracer(origin, fwd, ability, color, floor, `${ctx.casterId}:${ctx.abilityId}`);
    },
  },

  ground_zone: {
    anticipate(ctx) {
      const { ability, origin, groundY, color, windupMs, fwd, abilityId } = ctx;
      const off = GROUND_ZONE_OFFSET[abilityId] ?? 0;
      const zx = origin.x + fwd.x * off;
      const zz = origin.z + fwd.z * off;
      // The glyph ring assembles OVER the zone it will own: spinning in from
      // above, contracting onto the authored radius as the wind-up runs. The
      // exact filled telegraph underneath is Telegraphs.js's job.
      const { mesh, u } = zoneRing(ctx.floor, { radius: ability.hit.radius });
      flatOnGround(mesh, zx, groundY + FX_LIFT + 1.6, zz);
      mesh.renderOrder = 7;
      const radius = ability.hit.radius;
      track(mesh, windupMs + 60, (m, t) => {
        const k = easeOutCubic(Math.min(t * (windupMs + 60) / windupMs, 1));
        m.scale.setScalar(Math.max(0.05, radius * (0.4 + 0.6 * k)));
        m.position.y = groundY + FX_LIFT + 1.6 * (1 - k);
        u.fade.value = 0.25 + 0.55 * k;
      });
    },
    strike(ctx) {
      const { ability, origin, groundY, color, floor, fwd, abilityId, tail, casterId } = ctx;
      const radius = ability.hit.radius;
      const durationMs = ability.hit.durationMs;
      const off = GROUND_ZONE_OFFSET[abilityId] ?? 0;
      const zx = origin.x + fwd.x * off;
      const zz = origin.z + fwd.z * off;
      const gy = groundY + FX_LIFT;
      // The stamp: the ring pops at the exact radius…
      popRing(zx, gy + DECAL_STACK, zz, radius, floor, 240 + tail);
      // …something was PLACED: a light, a ring of deck dust, sparks rising.
      fx.flash({ x: zx, y: gy + 1, z: zz }, color, 28, radius + 4, 320);
      fx.dust(zx, gy, zz, 12, radius * 0.8);
      fx.rise({ x: zx, y: gy + 0.6, z: zz }, color, 8);
      const repeat = (ability.effects ?? []).find((e) => e.kind === 'repeat');
      const suppress = (ability.effects ?? []).some((e) => e.kind === 'suppress_abilities');
      if (isTrapAbility(ability)) {
        // Tracking Pixel: a rune that is PLACED first (dim outline, unarmed —
        // the sim will not fire it for TRAP_ARM_MS) and then ARMS (filled,
        // its keyline pulsing). It lives until it fires (the sim's `hit`
        // ends it through snapTrap) or until its 12 s run out.
        const { mesh, u } = runeTrap(floor);
        flatOnGround(mesh, zx, gy, zz);
        mesh.renderOrder = 5;
        const zone = { casterId, abilityId, mesh, u, x: zx, y: gy, z: zz, radius, fired: false, firedAt: 0 };
        _zones.push(zone);
        track(mesh, durationMs, (m, t) => {
          const now = performance.now();
          const age = t * durationMs;
          m.scale.setScalar(radius * popScale(Math.min(1, age / 240), 1, 0.25));
          u.armed.value = age >= TRAP_ARM_MS ? 1 : 0;
          if (zone.fired) {
            u.erode.value = Math.min(1, (now - zone.firedAt) / 220);
            if (u.erode.value >= 1) m.visible = false;
          } else {
            u.erode.value = t > 0.9 ? erosionAt(t, 0.9) : 0;
          }
        }, () => {
          const i = _zones.indexOf(zone);
          if (i >= 0) _zones.splice(i, 1);
        });
        return;
      }
      // …then the duration marker owns the ground for the zone's whole life,
      // holding full strength until the last 15% (the runeCircle doctrine).
      const { mesh, u } = zoneRing(floor, { radius });
      flatOnGround(mesh, zx, gy, zz);
      mesh.scale.setScalar(radius);
      mesh.renderOrder = 5;
      track(mesh, durationMs, (m, t) => {
        u.fade.value = t > 0.85 ? (1 - t) / 0.15 : 1;
      });
      if (suppress) nullField(zx, groundY, zz, radius, floor, durationMs);
      if (repeat && luxe()) {
        // Starlink's shells: decorative streaks INSIDE the true circle. The
        // sim rolls its own impact points — these are flourish, deliberately
        // small and ringless so they never claim a specific splash footprint.
        for (let i = 0; i < repeat.count; i++) {
          const a = Math.random() * Math.PI * 2;
          const r = Math.random() * radius * 0.75;
          const sx = zx + Math.sin(a) * r;
          const sz = zz + Math.cos(a) * r;
          scheduleDecoration(performance.now() + 200 + i * repeat.intervalMs,
            { x: sx, z: sz }, () => shellDrop(sx, groundY, sz, color, floor));
        }
      }
    },
  },

  self: {
    anticipate(ctx) {
      const { origin, groundY, color, windupMs } = ctx;
      // Inward, always inward — nothing leaves the caster because nothing
      // leaves the caster. The hex contracts onto the body.
      const { mesh, u } = hexRing(ctx.floor);
      flatOnGround(mesh, origin.x, groundY + FX_LIFT, origin.z);
      mesh.renderOrder = 6;
      track(mesh, windupMs + 60, (m, t) => {
        const k = Math.min(t * (windupMs + 60) / windupMs, 1);
        m.scale.setScalar(Math.max(0.4, 2.6 - easeOutCubic(k) * 1.5));
        u.fade.value = 0.3 + 0.6 * k;
      });
      gatherShards(ctx, 1.8, nCount(8));
    },
    strike(ctx) {
      const { ability, origin, groundY, color, floor, tail, casterId } = ctx;
      const gy = groundY + FX_LIFT;
      const kinds = new Set((ability.effects ?? []).map((e) => e.kind));
      // The hex pop at the feet: "something happened to THIS body".
      const { mesh, u } = hexRing(floor);
      flatOnGround(mesh, origin.x, gy, origin.z);
      mesh.renderOrder = 6;
      track(mesh, 300 + tail, (m, t) => {
        m.scale.setScalar(Math.max(0.3, 1.9 * easeOutCubic(Math.min(t * 2.2, 1))));
        u.erode.value = erosionAt(t, 0.4);
      });
      // What STAYS on the body (the shield, the speed glyph, the exhaust, the
      // fade shimmer) is the StatusAura's: it reads the effect off the
      // snapshot for exactly as long as the sim keeps it, on every screen.
      // Here is only the moment of the cast, per ability.
      if (kinds.has('damage_reduction')) {
        // Walled Garden slams DOWN: a shock band at the feet, a ring of deck
        // dust, chunks, a hard light and a shove of shake. The shell itself
        // pops in on the aura's first frame.
        fx.shockRing({ x: origin.x, y: gy, z: origin.z }, 1.9, floor, 320);
        fx.dust(origin.x, gy, origin.z, 10, 1.8);
        fx.chunks({ x: origin.x, y: gy, z: origin.z }, floor, 4, gy);
        fx.flash({ x: origin.x, y: origin.y + 0.6, z: origin.z }, color, 16, 5, 220);
        fx.shake(0.28, origin);
        return;
      }
      if (kinds.has('hover')) {
        // Hover Slam's launch: the exhaust column blasts down, dust kicked out
        // in a ring, a quick shock line — and the aura is told which deck it
        // took off from, so the blob shadow lands on the right floor.
        const { mesh: jet, u: ju } = column(color);
        ju.spin.value = Math.PI;
        jet.position.set(origin.x, origin.y - 0.9, origin.z);
        jet.renderOrder = 9;
        track(jet, 260, (m, t) => {
          m.scale.set(1.3 * popScale(t, 0.2, 0.4), 2.4 * (1 - t * 0.5), 1);
          ju.erode.value = erosionAt(t, 0.35);
        });
        fx.dust(origin.x, gy, origin.z, 12, 2.6);
        fx.shockRing({ x: origin.x, y: gy, z: origin.z }, 1.6, floor, 280);
        fx.flash({ x: origin.x, y: origin.y, z: origin.z }, color, 18, 5, 260);
        // The deck it LEFT: the cast-time ground. By the strike the predicted
        // body has already made its 8 u jump, so the anchor's ground is in the air.
        setHoverFloor(bodyOf(casterId), ctx.castGroundY ?? groundY);
        return;
      }
      if (kinds.has('invisible')) {
        // Ludicrous Mode blinks OUT: three afterimage smears peel off the body
        // in the identity colour, a glitch of shards, a puff, and a haze ring
        // pops on the deck. The faded body's own aura carries the shimmer.
        blinkSmears(origin, color, 3, 1.1);
        fx.burst(origin, color, { count: 10, speed: 4, floorY: groundY });
        fx.puffs({ x: origin.x, y: origin.y - 0.2, z: origin.z }, color, 4, { up: 0.6, spread: 0.6, size: 0.4, life: 0.45 });
        const { mesh: haze, u: hu } = hazeRing(color);
        flatOnGround(haze, origin.x, gy + DECAL_STACK, origin.z);
        haze.renderOrder = 6;
        track(haze, 380, (m, t) => {
          m.scale.setScalar(1.6 * popScale(t, 0.2, 0.5));
          hu.fade.value = 1 - t * t;
        });
        fx.flash(origin, color, 12, 4, 200);
        return;
      }
      if (kinds.has('decoy')) {
        // Autopilot SPLITS: two smears slide apart from the body, a glitch of
        // red and white shards, a pale puff — the copy steps out of the
        // original. The decoy body itself gets its blink-in from the
        // remote-player path when it appears.
        blinkSmears(origin, color, 2, 1.3);
        fx.burst(origin, color, { count: 8, speed: 3.5, floorY: groundY });
        fx.puffs({ x: origin.x, y: origin.y - 0.3, z: origin.z }, color, 3, { up: 0.5, spread: 0.7, size: 0.38, life: 0.4, pale: true });
        fx.flash(origin, color, 12, 4, 200);
        return;
      }
      // Overclock (and any other pure buff): ignition. A burst of electric
      // ticks off the body, sparks rising, a light. The speed glyph and the
      // ticks that follow are the aura's.
      for (let i = 0; i < nCount(4); i++) {
        const { mesh: bolt, u: bu } = boltBeam(color);
        const a = Math.random() * Math.PI * 2;
        bolt.position.set(origin.x + Math.cos(a) * 0.7, origin.y + Math.random() * 1.2 - 0.3, origin.z + Math.sin(a) * 0.7);
        bolt.rotation.set(Math.random() - 0.5, a + Math.PI / 2, Math.random() * Math.PI);
        bolt.scale.set(0.45, 0.9 + Math.random() * 0.6, 1);
        bolt.renderOrder = 10;
        bolt.material.depthTest = false;
        track(bolt, 120 + i * 25, (m, t) => { bu.fade.value = 1 - t * t; });
      }
      fx.rise(origin, color, 10);
      fx.flash({ x: origin.x, y: origin.y + 0.4, z: origin.z }, color, 16, 5, 240);
    },
  },
};

/** The rendered body of a player, or null. */
function bodyOf(playerId) {
  if (!playerId) return null;
  return playerId === session.userId ? localPlayer.mesh : remotePlayers.get(playerId) ?? null;
}

/**
 * Afterimage smears radiating from a body — the blink-out, the decoy split.
 * `n` smears, evenly spread with a random phase, each sliding outward along
 * its own heading as it erodes from the tail.
 *
 * Self abilities are body-anchored, so track() parents each smear into the
 * anchor group and re-expresses its position in the group's space. Every
 * position written AFTER track() must be in that same space: the first
 * version wrote world coordinates each frame and the smears sat at twice
 * the caster's position — Ludicrous Mode's blink-out never appeared on the
 * body. The base is read back from the mesh after track(), and the slide is
 * an offset from it (the anchor never rotates, so offsets are the same in
 * both spaces).
 */
function blinkSmears(origin, colorHex, n, reach) {
  const phase = Math.random() * Math.PI * 2;
  for (let i = 0; i < n; i++) {
    const a = phase + (i / n) * Math.PI * 2;
    const dx = Math.sin(a);
    const dz = Math.cos(a);
    const { mesh, u } = smear(colorHex);
    const yaw = Math.atan2(-dx, -dz);
    flatOnGround(mesh, origin.x, origin.y + 0.15, origin.z, yaw);
    mesh.rotation.x += 0.3;
    mesh.renderOrder = 8;
    const base = new THREE.Vector3();
    track(mesh, 220, (m, t) => {
      const d = easeOutCubic(t) * reach;
      m.position.set(base.x + dx * d, base.y, base.z + dz * d);
      m.scale.set(0.75, 1.9 - t * 0.6, 1);
      u.erode.value = erosionAt(t, 0.3);
    });
    base.copy(mesh.position);
  }
}

/**
 * The two colours every effect is drawn in, and the character's weight.
 *
 *   color  `colorHex` — the identity. Sparks, cores, lights, the impact star,
 *          the swing arc: everything that is the ABILITY.
 *   floor  `telegraphHex` — the floor-safe derivative. Rings, zone markers,
 *          line walls, domes: everything painted ON THE DECK, which already
 *          spends red on hazard and green on mend.
 *   heavy  the juggernaut: its swings and slams get weight (dust, chunks, a
 *          shove of shake) where the assassin's snap.
 */
function palette(characterId) {
  const def = CHARACTERS[characterId] ?? CHARACTERS.volt;
  return {
    color: def.colorHex,
    floor: telegraphHex(def.id),
    heavy: def.archetype === 'juggernaut',
  };
}

/** Chain Lightning / Robotaxi: the sim strikes ONE nearest target. */
function nearestOnly(ability) {
  return (ability.effects ?? []).some((e) => e.kind === 'chain' || e.kind === 'swap_position');
}

/** The anticipation gather: shards converging on the caster, timed to arrive
 *  on the strike beat. Approximate by design — it is flourish, not geometry.
 *  Through the kit's shard system (one draw for every shard in the game), not
 *  the legacy per-burst mesh pass 1 used here. */
function gatherShards(ctx, radius, count) {
  const { origin, color, windupMs } = ctx;
  if (!allowDecoration(origin)) return;
  fx.implode({ x: origin.x, y: origin.y - 0.3, z: origin.z }, radius, color, count, Math.max(windupMs / 1000, 0.12));
}

/**
 * A Starlink shell: a cel bomb falling in from above with a pale exhaust
 * ribbon and a blob shadow that grows on the deck as it comes down, then a
 * small blast. The shadow is the warning — it is on the ground before the
 * shell is.
 */
function shellDrop(x, groundY, z, colorHex, floorHex = colorHex) {
  const drop = 12;
  const fallMs = 240;
  const { mesh, u } = shellShape(colorHex);
  mesh.position.set(x, groundY + drop, z);
  mesh.scale.setScalar(0.7);
  mesh.renderOrder = 9;
  const freeRibbon = ribbonOwner(fx.ribbon(colorHex, 0.4, 2.6, 0.55));
  track(mesh, fallMs, (m, t) => {
    m.position.y = groundY + drop * (1 - t * t);
    u.spin.value += 0.12;
    fx.ribbonPush(freeRibbon.id, m.position);
    fx.shadow(m.position, groundY, 0.4 + 0.5 * t, 0.2 + 0.4 * t);
  }, freeRibbon);
  // The blast is decoration and may be culled under budget; the ribbon is
  // freed by the track's onEnd either way.
  scheduleDecoration(performance.now() + fallMs, { x, z }, () => {
    fx.explosion({ x, y: groundY, z }, 1.4, colorHex, { floorColor: floorHex, floorY: groundY });
  });
}

// ─── Public entry points ────────────────────────────────────────

/**
 * The cast event, at the START of the wind-up. Anticipation plays now;
 * the strike is scheduled onto the wind-up clock and fires when the sim
 * resolves the hit. `origin` is where main.js decided this cast lives (event
 * x/z for world-anchored, the caster's rendered transform for body-anchored).
 */
export function playAbilityCast(abilityId, characterId, casterId, origin, yaw) {
  const ability = findAbility(characterId, abilityId);
  const { color, floor, heavy } = palette(characterId);
  const fwd = forwardFromYaw(yaw ?? 0);

  // The universal dash: no AbilityDef, no hit to agree with. The event carries
  // only yaw, and a sideways dash travels sideways — so the departure dust
  // fires now, and the speed smears wait ~60 ms for the body to have MOVED,
  // then peel off opposite the travel it actually took.
  if (!ability) {
    if (abilityId === 'dash') {
      const feetY = origin.y - PLAYER_HALF_HEIGHT;
      fx.dust(origin.x, feetY, origin.z, 5, 1.8);
      const anchor = beginBodyAnchor('dash', casterId, origin, Infinity, 140);
      const from = { x: origin.x, z: origin.z };
      _pending.push({
        at: performance.now() + 60,
        fire: () => {
          const p = anchor.group.position;
          let dx = p.x - from.x;
          let dz = p.z - from.z;
          const len = Math.hypot(dx, dz);
          if (len > 0.05) { dx /= len; dz /= len; } else { dx = fwd.x; dz = fwd.z; }
          fx.dash({ x: p.x, y: p.y, z: p.z }, { x: dx, z: dz }, color, { floorY: p.y - PLAYER_HALF_HEIGHT });
          anchor.freezeAt = 0;
        },
      });
    } else if (allowDecoration(origin)) {
      fx.burst(origin, color, { count: nCount(10), speed: 5, floorY: origin.y - PLAYER_HALF_HEIGHT });
    }
    return;
  }

  const arch = ARCHETYPES[ability.hit.kind];
  if (!arch) return;

  const windupMs = ability.windupMs ?? 0;
  // A lunge cuts when the roll ARRIVES (the sim's arrival cast), so its
  // strike waits out the travel as well as the wind-up. Every other shape
  // strikes at wind-up end; a dash's own travel is drawn by `playTravel`.
  const strikeMs = windupMs + lungeMs(ability);
  const ctx = {
    ability,
    abilityId,
    characterId,
    casterId,
    color,
    floor,
    heavy,
    origin: { x: origin.x, y: origin.y, z: origin.z },
    groundY: origin.y - PLAYER_HALF_HEIGHT,
    /** The ground at the CAST, kept when a body anchor moves the strike origin. */
    castGroundY: origin.y - PLAYER_HALF_HEIGHT,
    yaw: yaw ?? 0,
    fwd,
    windupMs,
    recoveryMs: ability.recoveryMs ?? 0,
    tail: Math.max(ability.recoveryMs ?? 0, 160),
  };

  // Body-anchored casts ride the caster's rendered transform for the whole
  // three-beat envelope; `holdUntil` keeps the anchor alive until the strike
  // has spawned into it. Blink Strike freezes at the dash (see beginBodyAnchor).
  let anchor = null;
  if (abilityAnchor(ability) === 'body') {
    anchor = beginBodyAnchor(
      abilityId,
      casterId,
      ctx.origin,
      abilityId === 'blink_strike' ? windupMs : Infinity,
      strikeMs + 300
    );
  }

  if (windupMs > 0) {
    _buildAnchor = anchor;
    try {
      arch.anticipate?.(ctx);
    } finally {
      _buildAnchor = null;
    }
  }
  if (strikeMs > 0) {
    _pending.push({
      at: performance.now() + strikeMs,
      fire: () => fireStrike(arch, ctx, anchor),
    });
  } else {
    // Zero wind-up, no travel: no anticipation beat exists to draw. Strike now.
    fireStrike(arch, ctx, anchor);
  }
}

/** How long a melee lunge rolls before its cone is cut, in ms (0 if it does not travel). */
export function lungeMs(ability) {
  if (!ability || ability.hit.kind !== 'melee_cone') return 0;
  return (ability.selfMove ?? 0) > 0 ? ability.selfMoveMs ?? 0 : 0;
}

/**
 * The strike beat. For a body ability the origin is re-read from the anchor's
 * LIVE position — the sim resolves from where the caster IS at the resolve
 * tick, not where the cast began (a walking Monolith's slam lands under his
 * feet, not 1.5m behind them).
 */
function fireStrike(arch, ctx, anchor) {
  if (anchor) {
    ctx.origin = {
      x: anchor.group.position.x,
      y: anchor.group.position.y,
      z: anchor.group.position.z,
    };
    ctx.groundY = ctx.origin.y - PLAYER_HALF_HEIGHT;
  }
  _buildAnchor = anchor && _anchors.includes(anchor) ? anchor : null;
  try {
    arch.strike?.(ctx);
  } finally {
    _buildAnchor = null;
    // The sim resolved the hit HERE. A strike shape that kept following a
    // walking caster would drag the damage marker off the ground actually
    // damaged — the same reason Telegraphs freeze their decals at resolve.
    // (Buff shells that must keep following ride their own anchor — see
    // the self archetype.)
    if (anchor) anchor.freezeAt = 0;
  }
}

/**
 * Impact VFX for a resolved hit. Driven by the server's `hit` event. `dir` is
 * the drawn bullet's heading when the caller knows it — the shards then fly
 * back from the surface the way a real impact throws them.
 */
export function playHitVFX(abilityId, characterId, point, heavy, attackerId = null, dir = null) {
  const { color, floor } = palette(characterId);
  const ability = findAbility(characterId, abilityId);
  if (ability?.hit.kind === 'projectile') {
    // A shell that hit a body detonates HERE, at the sim's own impact — and
    // the predicted tracer, still flying its arc, is told it already went off.
    const now = performance.now();
    const key = attackerId ? `${attackerId}:${abilityId}` : null;
    const tracer = key ? _tracers.get(key) : null;
    if (tracer && now <= tracer.until) {
      tracer.detonated = true;
      _tracers.delete(key);
      // The predicted shell already landed and went off. The server's `hit`
      // for its splash is the same blast arriving late — for your own shot a
      // whole round-trip late — and drawing it again, centred on the victim,
      // put a second ring where the splash never was.
      if (tracer.landed) return;
    }
    const last = attackerId ? _lastDetonation.get(attackerId) ?? -Infinity : -Infinity;
    if (now - last > DETONATION_DEDUPE_MS) {
      if (attackerId) _lastDetonation.set(attackerId, now);
      const floorY = point.y - PLAYER_HALF_HEIGHT;
      fx.explosion({ x: point.x, y: floorY, z: point.z }, ability.hit.radius, color, { floorColor: floor, floorY });
      return;
    }
  }
  if (isTrapAbility(ability)) {
    // The trap fired: the rune SNAPS. Its marker erodes out (it lied for the
    // rest of its 12 s before), an ice burst at the victim's feet says SLOWED,
    // and the star says whose trap it was.
    snapTrap(attackerId, abilityId, point);
    const floorY = point.y - PLAYER_HALF_HEIGHT;
    fx.impact(point, color, { onBody: true, count: 8, star: 2.4, floorY });
    fx.frost({ x: point.x, y: floorY, z: point.z }, SLOW_HEX, 10);
    fx.flash(point, color, 16, 5, 220);
    return;
  }
  // The kit's impact: the burst star says WHERE in the attacker's colour, the
  // shards, puffs and sparks say it HURT. Heavier hits throw more.
  // A heavy hit is ONE bigger star (two stacked read as a flower), more
  // shards and a light. Never a floor ring: a hoop at the victim's feet reads
  // as an AoE footprint that does not exist.
  fx.impact(point, color, { onBody: true, count: heavy ? 10 : 6, dir, star: heavy ? 2.9 : undefined });
  if (heavy) fx.flash(point, color, 14, 5, 180);
}

/**
 * The sim's `hit` for a placed trap: end the caster's live trap marker
 * nearest the hit (the victim stood on it). The rune erodes over ~220 ms and
 * is forgotten. No-op if that caster has no live trap near the point.
 */
export function snapTrap(casterId, abilityId, point = null) {
  let best = null;
  let bestD2 = Infinity;
  for (const z of _zones) {
    if (z.fired || z.casterId !== casterId || z.abilityId !== abilityId) continue;
    const d2 = point ? (z.x - point.x) ** 2 + (z.z - point.z) ** 2 : 0;
    if (point && d2 > (z.radius + 1.5) ** 2) continue;
    if (d2 < bestD2) { best = z; bestD2 = d2; }
  }
  if (!best) return false;
  best.fired = true;
  best.firedAt = performance.now();
  _zones.splice(_zones.indexOf(best), 1);
  return true;
}

/** For tests: the live (unfired) trap markers as `casterId:abilityId`. */
export function placedZones() {
  return _zones.map((z) => `${z.casterId}:${z.abilityId}`);
}

/**
 * A decoy left the fight (shot, or its 4 s ran out): a cartoon POOF in the
 * trickster's colour — puffs, a glitch of red and white shards, a small star
 * — where the body was. Called by the remote-player path on removal.
 */
export function playDecoyPop(point, characterId) {
  const { color, floor } = palette(characterId);
  const feetY = point.y - PLAYER_HALF_HEIGHT;
  popStar(point.x, point.y + 0.2, point.z, 1.7, color, 200);
  fx.puffs({ x: point.x, y: point.y - 0.2, z: point.z }, color, 6, { up: 1.2, spread: 0.7, size: 0.5, life: 0.5, floorY: feetY });
  fx.puffs({ x: point.x, y: point.y, z: point.z }, color, 3, { up: 1.5, spread: 0.5, size: 0.42, life: 0.45, pale: true, floorY: feetY });
  fx.burst(point, color, { count: 12, speed: 4.5, floorY: feetY });
  fx.shockRing({ x: point.x, y: feetY, z: point.z }, 1.3, floor, 260);
  fx.flash(point, color, 14, 5, 220);
}

/**
 * A body that was gone for a moment is back (a faded trickster reappearing,
 * a decoy stepping out of its original): a blink-IN — smears converging on
 * the body, a small star, a puff.
 */
export function playBlinkIn(point, characterId) {
  const { color } = palette(characterId);
  const phase = Math.random() * Math.PI * 2;
  for (let i = 0; i < 3; i++) {
    const a = phase + (i / 3) * Math.PI * 2;
    const dx = Math.sin(a);
    const dz = Math.cos(a);
    const { mesh, u } = smear(color);
    const yaw = Math.atan2(dx, dz);
    flatOnGround(mesh, point.x, point.y + 0.15, point.z, yaw);
    mesh.rotation.x += 0.3;
    mesh.renderOrder = 8;
    // Same rule as blinkSmears: offsets from the post-track() base, never world writes.
    const base = new THREE.Vector3();
    track(mesh, 200, (m, t) => {
      const d = (1 - easeOutCubic(t)) * 1.2;
      m.position.set(base.x + dx * d, base.y, base.z + dz * d);
      m.scale.set(0.7, 1.6, 1);
      u.erode.value = erosionAt(t, 0.45);
    });
    base.copy(mesh.position);
  }
  popStar(point.x, point.y + 0.3, point.z, 1.3, color, 180);
  fx.puffs({ x: point.x, y: point.y - 0.3, z: point.z }, color, 3, { up: 0.6, spread: 0.6, size: 0.36, life: 0.4, pale: true });
  fx.flash(point, color, 10, 4, 180);
}

/**
 * A body (re)spawns: a column of its colour beams DOWN onto the deck, a hex
 * pop at the feet, deck dust, sparks rising, a light. Your own respawn and
 * every opponent's, from the same call.
 */
export function playSpawnIn(point, characterId) {
  const { color, floor } = palette(characterId);
  const feetY = point.y - PLAYER_HALF_HEIGHT;
  const gy = feetY + FX_LIFT;
  const { mesh, u } = column(color);
  mesh.position.set(point.x, feetY + 2.6, point.z);
  mesh.renderOrder = 9;
  track(mesh, 380, (m, t) => {
    // Tall and thin on frame one, settling onto the body, then eroding up.
    const k = easeOutCubic(Math.min(1, t * 2.5));
    m.scale.set(0.9 + k * 0.9, 5.2 - k * 1.4, 1);
    m.position.y = feetY + 2.6 - k * 0.6;
    u.erode.value = erosionAt(t, 0.45);
  });
  const { mesh: hex, u: hu } = hexRing(floor);
  flatOnGround(hex, point.x, gy, point.z);
  hex.renderOrder = 6;
  track(hex, 420, (m, t) => {
    m.scale.setScalar(Math.max(0.3, 2.1 * easeOutCubic(Math.min(t * 2, 1))));
    hu.erode.value = erosionAt(t, 0.45);
  });
  fx.dust(point.x, gy, point.z, 8, 1.6);
  fx.rise(point, color, 8);
  fx.flash({ x: point.x, y: point.y + 0.5, z: point.z }, color, 18, 6, 320);
}

// ─── Hit flash on the struck body ───────────────────────────────
//
// The impact star says WHERE the hit landed; the body flash says WHO was hit.
// Without it, a hit on a moving target reads as a puff near a character
// rather than damage on them — v1 flashed the victim and it was load-bearing.

/** The white-out relaxes at this rate (per second): ~140 ms to zero. */
const HIT_FLASH_DECAY = 7;
/** playerId -> { mesh, amount }. Re-hitting restarts the pulse. */
const _hitFlashes = new Map();

/**
 * Pulse the struck player's body white for one beat. Driven by the server's
 * `hit` event (the caller passes the event's targetId) — purely presentational.
 * `point` (the hit's world position) runs the ripple over a live shield.
 *
 * Goes through ToonMaterials.setFlashAmount, which mixes a per-material TSL
 * uniform toward white. Never touch `.emissive` here: these are node materials
 * with a `colorNode`, and the scalar emissive path is not consulted.
 */
export function flashHitTarget(playerId, point = null) {
  if (typeof ToonMaterials.setFlashAmount !== 'function') return;
  const mesh = playerId === session.userId ? localPlayer.mesh : remotePlayers.get(playerId);
  if (!mesh) return;
  _hitFlashes.set(playerId, { mesh, amount: 1 });
  ToonMaterials.setFlashAmount(mesh, 1);
  // The flash says WHO; the squash says it HURT. Weighted by archetype.
  applyHitSquash(mesh);
  if (point) shieldHit(mesh, point);
}

function updateHitFlashes() {
  if (_hitFlashes.size === 0) return;
  const dt = frame.delta;
  for (const [id, flash] of _hitFlashes) {
    // A body that left the scene (the killing blow removes it) must not keep
    // receiving uniform writes into materials that have been disposed.
    if (!flash.mesh.parent) {
      _hitFlashes.delete(id);
      continue;
    }
    flash.amount -= dt * HIT_FLASH_DECAY;
    if (flash.amount <= 0) {
      ToonMaterials.setFlashAmount(flash.mesh, 0);
      _hitFlashes.delete(id);
    } else {
      ToonMaterials.setFlashAmount(flash.mesh, flash.amount);
    }
  }
}

/**
 * Chain Lightning's arcs: one THICK jagged cel bolt per link, between the
 * resolved targets, with an ink outline and a fork (CelShapes.boltBeam).
 * Each hop FLASHES: the bolt re-seeds its zigzag three times over its life
 * and its brightness strobes (full, dim, full, out), the way current does,
 * and the victim end gets a star, a column of discharge, sparks and a light.
 */
export function playChainArc(from, to, characterId) {
  const { color } = palette(characterId);
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 0.001;
  const { mesh, u } = boltBeam(color);
  mesh.scale.set(1.9, len, 1);
  mesh.position.set(from.x + dx / 2, from.y + dy / 2, from.z + dz / 2);
  mesh.lookAt(to.x, to.y, to.z);
  mesh.rotateX(Math.PI / 2);
  // Draws through the bodies it joins: a bolt hidden by a shoulder is no bolt.
  mesh.material.depthTest = false;
  mesh.renderOrder = 10;
  mesh.userData.reseeds = 0;
  track(mesh, 320, (m, t) => {
    // Strobe: 1 → 0.35 → 1 → 0.5 → 0, on a 4-beat flicker over the life.
    const beat = t * 4;
    const f = beat < 1 ? 1 : beat < 1.6 ? 0.35 : beat < 2.5 ? 1 : beat < 3.2 ? 0.55 : Math.max(0, (4 - beat) / 0.8);
    u.fade.value = f;
    const stage = Math.floor(t * 3);
    if (stage > m.userData.reseeds) {
      m.userData.reseeds = stage;
      u.seed.value = Math.random() * 10;
    }
  });
  // The link's own colour, not white: a white identity now means "hueless"
  // to the kit and would come out in the steel accent.
  popStar(to.x, to.y + 0.2, to.z, 1.8, color, 220);
  pillar(to.x, to.y - PLAYER_HALF_HEIGHT, to.z, 1.3, 3.0, color, 300);
  fx.impact(to, color, { onBody: true, count: 8 });
  fx.flash(to, color, 16, 5, 200);
}

/** Elimination: the big cartoon POOF — a cloud of the fallen colour, confetti
 *  shards, a burst star and a floor band — while the body spins and shrinks
 *  to nothing (pose.js / SkinnedCharacter). */
export function playEliminationVFX(point, characterId) {
  const { color, floor } = palette(characterId);
  const feetY = point.y - PLAYER_HALF_HEIGHT;
  fx.defeat(point, color, { floorColor: floor, floorY: feetY });
}

// ─── Ballistic tracer ───────────────────────────────────────────

/**
 * A predicted shell for lobbed projectiles. Snapshots do not carry projectile
 * positions, so without this the shot is invisible from muzzle to impact and
 * the victim's first cue is the damage number. The path is precomputed with
 * the sim's own tick math (gravity before move, spawn offset, range spent
 * after moving) from the cast event's frozen yaw. Fired at STRIKE, because the
 * sim spawns the shell at resolve. Purely presentational: on an open lane it
 * lands where the real shell lands; a wall mid-flight stops the real one early
 * and this one flies through — the `hit` event still marks the true impact.
 */
function ballisticTracer(origin, fwd, ability, colorHex, floorHex, key = null) {
  const shape = ability.hit;
  const floorY = origin.y - PLAYER_HALF_HEIGHT;

  const pts = [];
  let vy = shape.speed * PROJECTILE_LAUNCH_SIN;
  const vh = shape.speed * PROJECTILE_LAUNCH_COS;
  let x = origin.x + fwd.x * PROJECTILE_SPAWN_OFFSET;
  let y = origin.y + 0.4;
  let z = origin.z + fwd.z * PROJECTILE_SPAWN_OFFSET;
  let range = shape.maxRange;
  pts.push(x, y, z);
  for (let i = 0; i < 300 && y > floorY && range > 0; i++) {
    vy -= shape.gravity * DT;
    y += vy * DT;
    const step = vh * DT;
    x += fwd.x * step;
    z += fwd.z * step;
    range -= step;
    pts.push(x, y, z);
  }
  const flightMs = (pts.length / 3 - 1) * DT * 1000;

  // The shell: a cel bomb that spins as it flies, a pale exhaust ribbon
  // behind it, a blob shadow on the deck under it that grows as it comes
  // down — the landing spot is on the floor before the shell is.
  let apex = pts[1];
  for (let i = 4; i < pts.length; i += 3) if (pts[i] > apex) apex = pts[i];
  const { mesh, u } = shellShape(colorHex);
  mesh.name = 'tracer-shell';
  mesh.scale.setScalar(0.9);
  mesh.position.set(pts[0], pts[1], pts[2]);
  mesh.renderOrder = 9;
  const freeRibbon = ribbonOwner(fx.ribbon(colorHex, 0.5, 3.2, 0.55));

  const life = flightMs + 150;
  const state = { detonated: false, landed: false, until: performance.now() + life };
  if (key) _tracers.set(key, state);
  const land = (m) => {
    m.visible = false;
    freeRibbon();
    // Kept registered for a while as LANDED, so the server's late `hit` for
    // this splash knows the blast was already drawn (see playHitVFX).
    state.landed = true;
    state.until = performance.now() + LANDED_HIT_GRACE_MS;
    const last = pts.length - 3;
    // The detonation's band expands to EXACTLY the splash radius — it
    // marks the ground the sim damages, not decoration.
    fx.explosion({ x: pts[last], y: floorY, z: pts[last + 2] }, shape.radius, colorHex, {
      floorColor: floorHex,
      floorY,
    });
  };
  return track(mesh, life, (m, t) => {
    const flight = t * life;
    if (state.detonated) {
      // The sim's hit already detonated this shell; the prediction is over.
      freeRibbon();
      m.visible = false;
      return;
    }
    if (flight >= flightMs) {
      if (!state.landed) land(m);
      return;
    }
    u.spin.value += 0.09;
    const s = flight / 1000 / DT;
    const i0 = Math.min(Math.floor(s), pts.length / 3 - 2) * 3;
    const f = Math.min(s - Math.floor(s), 1);
    m.position.set(
      pts[i0] + (pts[i0 + 3] - pts[i0]) * f,
      pts[i0 + 1] + (pts[i0 + 4] - pts[i0 + 1]) * f,
      pts[i0 + 2] + (pts[i0 + 5] - pts[i0 + 2]) * f
    );
    fx.ribbonPush(freeRibbon.id, m.position);
    const h = Math.max(0, Math.min(1, (m.position.y - floorY) / Math.max(0.5, apex - floorY)));
    fx.shadow(m.position, floorY, 0.45 + 0.35 * (1 - h), 0.25 + 0.3 * (1 - h));
    fx.requestLight(m.position, colorHex, 2.4, 4.5);
  }, (reason) => {
    // A frame gap longer than the 150 ms tail skips the landing frame: land on
    // the way out, so the blast is drawn and the ribbon comes back.
    if (reason === 'expired' && !state.landed && !state.detonated) land(mesh);
    freeRibbon();
  });
}

function findAbility(characterId, abilityId) {
  const def = CHARACTERS[characterId];
  if (!def) return null;
  for (const slot of ['basic', 'skill1', 'skill2', 'ultimate']) {
    if (def.abilities[slot].id === abilityId) return def.abilities[slot];
  }
  return null;
}

// The shader prewarm lives in vfx/ShaderPrewarm.js: every pooled shape this
// module can acquire has a factory there, and vfx-pass3.test.js holds the two
// lists to each other. Nothing here fires fake abilities to warm up.

// ─── Travel beats ───────────────────────────────────────────────
//
// Every displacement in the roster TRAVELS now (a roll, a dash, a leap, a
// ride — `travel.ts` in @chaos/sim), and the sim reports each one as an
// `effect_applied` naming the travel and its exact duration. The beat rides
// the body as DRAWN (the same transform the mesh uses), so the trail is
// wherever the screen shows the body, local prediction or remote
// interpolation alike, and the arrival lands where it actually stopped —
// short at a wall, or at the body a roll met.

const TRAVEL_EFFECTS = Object.freeze(['lunge', 'blink', 'ride', 'leap']);

/** True for an `effect_applied` name that is a travel (and so gets `playTravel`). */
export function isTravelEffect(name) {
  return TRAVEL_EFFECTS.includes(name);
}

/**
 * Per kind: the ribbon the body drags (width, length, whiten, height off the
 * capsule centre), and the kick of deck dust it leaves from.
 *   lunge  NVDA's roll: short and low, a tuck-squash, smears at the feet.
 *   blink  Parallel Path: a long bright streak at chest height, sparks.
 *   ride   Robotaxi: a wide pale wake at the wheels, for both riders.
 *   leap   Hover Slam: a thruster trail under the body, a shadow below.
 */
const TRAVEL_STYLE = Object.freeze({
  lunge: { width: 0.3, length: 1.6, whiten: 0.1, y: -0.35, dust: 4 },
  blink: { width: 0.5, length: 6, whiten: 0.35, y: 0.1, dust: 5 },
  ride: { width: 0.45, length: 5, whiten: 0.25, y: -0.55, dust: 6 },
  leap: { width: 0.4, length: 4, whiten: 0.2, y: -0.8, dust: 0 },
});

/**
 * How long past the sim's own duration the beat keeps following the body. A
 * remote body is drawn ~100 ms behind the server, so it is still travelling
 * on screen when the travel has ended in the sim.
 */
const TRAVEL_TAIL_MS = 160;
/** A leap's landing is watched for this long (the 2 s hover plus the fall). */
const LANDING_WATCH_MS = 4000;

/** Robotaxi's wake is always the rider's colour — whoever's body it carries. */
function rideColor() {
  for (const def of Object.values(CHARACTERS)) {
    for (const slot of ['skill1', 'skill2', 'ultimate']) {
      if ((def.abilities[slot].effects ?? []).some((e) => e.kind === 'swap_position')) return def.colorHex;
    }
  }
  return CHARACTERS.mirage.colorHex;
}

/** Bodies being followed by a travel beat, and bodies waiting to land from a leap. */
const _followers = [];
const _landings = [];
const _followScratch = new THREE.Vector3();

/**
 * A travel began on `playerId`'s body: the sim's `effect_applied` for a
 * lunge, blink, ride or leap, with the travel's real duration. Returns
 * whether a beat started (false for a non-travel name or an unseen body).
 */
export function playTravel(kind, characterId, playerId, durationMs) {
  const style = TRAVEL_STYLE[kind];
  if (!style || !_scene) return false;
  const start = casterRenderPos(playerId, new THREE.Vector3());
  if (!start) return false;
  const { color, floor } = palette(characterId);
  const tint = kind === 'ride' ? rideColor() : color;
  const feetY = start.y - PLAYER_HALF_HEIGHT;
  const now = performance.now();
  // The tuck: the body squashes into the roll, and relaxes out of it.
  if (kind === 'lunge') applyHitSquash(bodyOf(playerId));
  if (style.dust > 0 && allowDecoration(start)) fx.dust(start.x, feetY, start.z, style.dust, 1.2);
  if (kind === 'ride') fx.flash(start, tint, 10, 4, 180);
  _followers.push({
    kind,
    playerId,
    color: tint,
    floor,
    style,
    until: now + Math.max(0, durationMs) + TRAVEL_TAIL_MS,
    from: start,
    last: start.clone(),
    smeared: false,
    ribbon: ribbonOwner(fx.ribbon(tint, style.width, style.length, style.whiten)),
    groundY: feetY,
  });
  return true;
}

function updateFollowers(now) {
  for (let i = _followers.length - 1; i >= 0; i--) {
    const f = _followers[i];
    const pos = casterRenderPos(f.playerId, _followScratch);
    if (pos) {
      // Speed smears once the body has visibly MOVED, peeled off opposite
      // the direction it actually went (a roll into a wall goes nowhere).
      if (!f.smeared) {
        const dx = pos.x - f.from.x;
        const dz = pos.z - f.from.z;
        const len = Math.hypot(dx, dz);
        if (len > 0.25 || (f.kind === 'leap' && pos.y - f.from.y > 0.25)) {
          f.smeared = true;
          if (len > 0.25) fx.dash(pos, { x: dx / len, z: dz / len }, f.color, { floorY: f.groundY });
        }
      }
      fx.ribbonPush(f.ribbon.id, { x: pos.x, y: pos.y + f.style.y, z: pos.z });
      if (f.kind === 'blink' && luxe()) fx.trail(pos, f.color, 0.8);
      if (f.kind === 'leap') fx.shadow(pos, f.groundY, 0.5, 0.35);
      f.last.copy(pos);
    }
    if (now < f.until) continue;
    _followers.splice(i, 1);
    f.ribbon();
    arriveTravel(f, now);
  }
  for (let i = _landings.length - 1; i >= 0; i--) {
    const l = _landings[i];
    const pos = casterRenderPos(l.playerId, _followScratch);
    if (!pos || now >= l.until) {
      _landings.splice(i, 1);
      continue;
    }
    // Watched from the hover down: falling, then still. The frame the fall
    // stops is the landing, wherever the deck turned out to be.
    if (pos.y < l.prevY - 0.02) l.falling = true;
    else if (l.falling && Math.abs(pos.y - l.prevY) < 0.005) {
      const feet = { x: pos.x, y: pos.y - PLAYER_HALF_HEIGHT, z: pos.z };
      fx.landing(feet, 0.9);
      fx.shockRing(feet, 1.4, l.floor, 260);
      _landings.splice(i, 1);
      continue;
    }
    l.prevY = pos.y;
  }
}

/** The end of a travel: what lands where the body stopped. */
function arriveTravel(f, now) {
  const at = f.last;
  const feetY = at.y - PLAYER_HALF_HEIGHT;
  switch (f.kind) {
    case 'blink':
      // Parallel Path's arrival: a burst at the body's real stop, lit.
      popStar(at.x, at.y, at.z, 1.8, f.color, 260);
      fx.impact(at, f.color, { count: 10, floorY: feetY });
      fx.flash(at, f.color, 12, 5, 200);
      break;
    case 'ride':
      // Dropped off: dust at the wheels and a small star.
      if (allowDecoration(at)) fx.dust(at.x, feetY, at.z, 6, 1.4);
      popStar(at.x, at.y, at.z, 1.2, f.color, 200);
      break;
    case 'leap':
      // At the apex the hover takes over (the aura's exhaust); the landing
      // dust waits for the fall to end.
      _landings.push({ playerId: f.playerId, floor: f.floor, prevY: at.y, falling: false, until: now + LANDING_WATCH_MS });
      break;
    default:
      // The roll's arrival IS its cut — the melee strike, on the cast clock.
      break;
  }
}

// ─── Frame update ───────────────────────────────────────────────

export function updateAbilityVFX() {
  const now = performance.now();
  updateHitFlashes();
  // Strike beats fire on the wind-up clock — before mesh updates, so a strike
  // spawned this frame renders this frame.
  for (let i = _pending.length - 1; i >= 0; i--) {
    if (now >= _pending[i].at) {
      const [p] = _pending.splice(i, 1);
      if (p.decoration) _decorationPending--;
      p.fire();
    }
  }
  for (let i = _live.length - 1; i >= 0; i--) {
    const fx = _live[i];
    const t = (now - fx.born) / fx.life;
    if (t >= 1) {
      fx.onEnd?.('expired');
      releaseEffect(fx);
      if (fx.decoration) _decorationLive--;
      _live.splice(i, 1);
      continue;
    }
    fx.update?.(fx.mesh, t);
  }
  updateAnchors(now);
  updateFollowers(now);
}

export function clearAbilityVFX() {
  _pending.length = 0;
  for (const f of _followers) f.ribbon();
  _followers.length = 0;
  _landings.length = 0;
  for (const fx of _live) {
    fx.onEnd?.('cleared');
    releaseEffect(fx);
  }
  _live.length = 0;
  _decorationLive = 0;
  _decorationPending = 0;
  _decorationCount = 0;
  _decorationWindow = -Infinity;
  _decorating = false;
  for (const a of _anchors) _scene.remove(a.group);
  _anchors.length = 0;
  _buildAnchor = null;
  _tracers.clear();
  _lastDetonation.clear();
  _zones.length = 0;
  if (typeof ToonMaterials.setFlashAmount === 'function') {
    for (const flash of _hitFlashes.values()) ToonMaterials.setFlashAmount(flash.mesh, 0);
  }
  _hitFlashes.clear();
}

function releaseEffect(fx) {
  if (releaseCelShape(fx.mesh)) return;
  fx.mesh.removeFromParent();
  if (!fx.sharedGeo) fx.mesh.geometry.dispose();
  fx.mesh.material.dispose();
}

/** Bounded scalar diagnostics; no per-cast history or retained effect objects.
 * Pool live also includes ShaderPrewarm's renderer-lifetime bundles. */
export function getVfxStats() {
  return {
    pool: getCelShapeStats(),
    effects: {
      live: _live.length,
      essential: _live.length - _decorationLive,
      decoration: _decorationLive,
      pending: _pending.length,
      pendingEssential: _pending.length - _decorationPending,
      pendingDecoration: _decorationPending,
      anchors: _anchors.length,
      travels: _followers.length,
      landings: _landings.length,
      hitFlashes: _hitFlashes.size,
    },
    decoration: { ..._decorationStats, limits: { ...DECORATION_LIMITS } },
  };
}
